import type { InstallationsQueryResult } from '@/data/installations/queries';
import type { DatabaseInstallation } from '@/types/github';
import type { SamlSsoInfo } from '@/utils/github/user-auth';
import { useQuery } from '@tanstack/react-query';

import { INSTALLATIONS_DATA_SOURCE } from './constants';

import { getInstallationsQueryFn, installationsQueryKey } from '@/data/installations/queries';
import { clientLogger } from '@/lib/client-logger';
import { useAuth } from '@/providers/auth-provider';

export type UseInstallationsResult = {
  data: DatabaseInstallation[];
  samlSso?: SamlSsoInfo;
  isLoading: boolean;
  isRefreshing: boolean;
  isPending: boolean;
  isPartiallyLoaded: boolean;
  isLoadingFromDb: boolean;
  isLoadingFromGithub: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
};

export function useInstallations(): UseInstallationsResult {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();

  const dbQuery = useQuery<InstallationsQueryResult>({
    queryKey: installationsQueryKey(INSTALLATIONS_DATA_SOURCE.DB),
    queryFn: () => getInstallationsQueryFn(INSTALLATIONS_DATA_SOURCE.DB),
    enabled: isAuthenticated,
    staleTime: Infinity,
    gcTime: Infinity,
    meta: { persist: true },
  });

  const githubQuery = useQuery<InstallationsQueryResult>({
    queryKey: installationsQueryKey(INSTALLATIONS_DATA_SOURCE.GITHUB),
    queryFn: () => getInstallationsQueryFn(INSTALLATIONS_DATA_SOURCE.GITHUB),
    enabled: isAuthenticated,
    staleTime: Infinity,
    gcTime: Infinity,
    meta: { persist: true },
  });

  const githubInstallations = githubQuery.data?.installations;
  const dbInstallations = dbQuery.data?.installations;
  const dbInstallationCount = dbInstallations?.length ?? 0;
  const githubInstallationCount = githubInstallations?.length ?? 0;
  // GitHub may return partial or empty installation lists for org members who already have
  // cubic seats, even when the API response does not include a detectable SAML SSO signal.
  const shouldPreferDbInstallations = dbInstallationCount > githubInstallationCount;
  // A pending GitHub query only blocks loading while the DB returned nothing:
  // consumers render from DB data immediately (GitHub reconciles in the
  // background) and zero-installation states are never shown prematurely.
  const isBlockingLoad = dbQuery.isLoading || (githubQuery.isLoading && dbInstallationCount === 0);

  const installations = shouldPreferDbInstallations
    ? (dbInstallations ?? githubInstallations ?? [])
    : (githubInstallations ?? dbInstallations ?? []);
  const samlSso = githubQuery.data?.samlSso ?? dbQuery.data?.samlSso;

  if (isAuthLoading) {
    return {
      data: [],
      samlSso: undefined,
      isLoading: true,
      isRefreshing: false,
      isPending: true,
      isPartiallyLoaded: false,
      isLoadingFromDb: false,
      isLoadingFromGithub: false,
      isError: false,
      error: null,
      refetch: () => {},
    };
  }

  if (!isAuthenticated) {
    clientLogger.info('[useInstallations] User not authenticated, returning empty array');
    return {
      data: [],
      samlSso: undefined,
      isLoading: false,
      isRefreshing: false,
      isPending: false,
      isPartiallyLoaded: false,
      isLoadingFromDb: false,
      isLoadingFromGithub: false,
      isError: false,
      error: null,
      refetch: () => {},
    };
  }

  const isPending = dbQuery.isPending || githubQuery.isPending;
  const refetch = () => {
    void dbQuery.refetch();
    void githubQuery.refetch();
  };

  return {
    data: installations,
    samlSso,
    isLoading: isBlockingLoad,
    isRefreshing: dbQuery.isRefetching || githubQuery.isRefetching,
    isPending,
    isPartiallyLoaded: dbQuery.isSuccess || githubQuery.isSuccess,
    isLoadingFromDb: dbQuery.isLoading,
    isLoadingFromGithub: githubQuery.isLoading,
    isError: githubQuery.isError,
    error: githubQuery.error,
    refetch,
  };
}
